import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import loginImage from '../../assets/images/login-image.png';
import logo from '../../assets/images/logo-1.svg';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ReactLoading from 'react-loading';
import { GenerateOtpAsync } from '../../API/Account/GenerateOtpAsync';
import { ValidateOtpAsync } from '../../API/Account/ValidateOtpAsync';
import { getUserProfile } from '../../API/UserProfileApi/GetUserProfile';
import { useLocalization } from "../Context/MultiLanguageContext";
import { getApiBaseUrl, setApiBaseUrl, PREPROD_API_BASE_URL, PROD_API_BASE_URL } from '../../utils/apiBaseUrl';
import { GoogleLogin } from "@react-oauth/google";
import { socialLogin } from '../../API/Auth/socialLogin';
import google from '../../assets/icons/googleLogo.svg';
import apple from '../../assets/icons/appleLogo.svg';
import guest from '../../assets/icons/guestLogo.svg';

declare global {
  interface Window {
    AppleID: any;
  }
}

const Signin = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [apiBaseUrl, setApiBaseUrlState] = useState(getApiBaseUrl());

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: "in.getmethere.pam.web",
        scope: "name email",
        redirectURI: window.location.origin + "/auth/apple/callback",
        usePopup: true,
      });
    }
  }, []);

  const handleSendOtp = async () => {
    if (!email) {
      toast.error(t("validEmail"));
      return;
    }

    if (!validateEmail(email)) {
      toast.error(t("invalidEmail"));
      return;
    }

    if (loading || cooldown) {
      return;
    }

    setCooldown(true);
    setLoading(true);

    try {
      const responseData = await GenerateOtpAsync(email, t);

      if (!responseData) {
        if (navigator.onLine) {
          toast.error(t("generateOtpFailed"), { toastId: "otp-failed" });
        }
        return;
      }

      if (!responseData.isRegisteredUser) {
        toast.success(t("notRegisteredMessage"));
      }

      setTimeout(() => {
        navigate("/register", {
          state: {
            email,
            otpSent: true,
            isRegisteredUser: responseData.isRegisteredUser,
            autoFillOtp: email === "guest@getmethere.in" ? "999999" : undefined
          },
        });
      }, 1500);
    } catch (error) {
      if (navigator.onLine) {
        toast.error(t("generateOtpFailed2"), { toastId: "otp-error" });
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
      setTimeout(() => {
        setCooldown(false);
      }, 3000);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);

    try {
      const responseData = await ValidateOtpAsync(
        "guest@getmethere.in",
        undefined,
        undefined,
        "999999"
      );

      if (!responseData) {
        toast.error(t("Invalidotp") || "Guest login failed");
        return;
      }

      const { token, refreshToken } = responseData;
      if (token) {
        localStorage.setItem("token", token);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }

        const profileData = await getUserProfile(t);
        const userName = profileData.userName;

        if (userName) {
          localStorage.setItem("userName", userName);
          // toast.success(t("loginSuccess") || "Logged in as Guest");

          setTimeout(() => {
            const redirectUrl = localStorage.getItem("redirectUrl");

            if (redirectUrl) {
              localStorage.removeItem("redirectUrl");
              window.location.href = redirectUrl;
            } else {
              navigate("/home", { replace: true });
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Error during guest login:", error);
      toast.error(t("loginFailed") || "Guest login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEnvironmentSwitch = (url: string) => {
    setApiBaseUrl(url);
    setApiBaseUrlState(url);
    window.location.reload();
  };

  const handleGoogleLogin = async (token: string) => {
    try {
      setLoading(true);

      const data = await socialLogin("google", token);

      if (!data?.token) {
        toast.error("Google login failed");
        return;
      }

      localStorage.setItem("token", data.token);

      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }

      try {
        const profileData = await getUserProfile(t);

        const userName = profileData?.userName;
        const phoneNumber = profileData?.phoneNumber;

        if (userName) {
          localStorage.setItem("userName", userName);
        }

        // ✅ if profile incomplete → go to profile page
        if (!userName || !phoneNumber) {
          navigate("/profile", { replace: true });
          return;
        }

      } catch (e) {
        console.log("Profile fetch failed, continuing...");
      }

      // ✅ complete profile → go home
      const redirectUrl = localStorage.getItem("redirectUrl");

      if (redirectUrl) {
        localStorage.removeItem("redirectUrl");
        window.location.href = redirectUrl; // go back to address page
      } else {
        navigate("/home", { replace: true });
      }

    } catch (error) {
      console.error(error);
      toast.error("Google login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      const response = await window.AppleID.auth.signIn();

      const token: string | undefined = response?.authorization?.id_token;

      if (!token) {
        toast.error("Apple login failed");
        return;
      }

      setLoading(true);

      const data = await socialLogin("apple", token);

      if (!data?.token) {
        toast.error("Apple login failed");
        return;
      }

      localStorage.setItem("token", data.token);

      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }

      const profileData = await getUserProfile(t);

      const userName = profileData?.userName;
      const phoneNumber = profileData?.phoneNumber;


      if (userName) {
        localStorage.setItem("userName", userName);
      }


      if (!profileData?.userName || !profileData?.phoneNumber) {
        navigate("/profile", { replace: true });
        return;
      }

      const redirectUrl = localStorage.getItem("redirectUrl");

      if (redirectUrl) {
        localStorage.removeItem("redirectUrl");
        window.location.href = redirectUrl;
      } else {
        navigate("/home", { replace: true });
      }

    } catch (error) {
      console.error("Apple login error:", error);
      toast.error("Apple login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendOtp();
  };

  return (
    <div className="content-wrapper select-none relative">
      <main>
        <div className="mx-auto max-w-screen-2xl h-screen flex items-center p-4 md:p-6 2xl:p-10">
          {process.env.REACT_APP_API_BASE_URL === PREPROD_API_BASE_URL && (
            <div className="absolute top-4 right-4">
              <div className="inline-flex rounded-lg border border-blue-200 bg-white p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => handleEnvironmentSwitch(PREPROD_API_BASE_URL)}
                  className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition ${apiBaseUrl === PREPROD_API_BASE_URL ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                  disabled={loading}
                >
                  {t("preprod") || "Preprod"}
                </button>
                <button
                  type="button"
                  onClick={() => handleEnvironmentSwitch(PROD_API_BASE_URL)}
                  className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition ${apiBaseUrl === PROD_API_BASE_URL ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                  disabled={loading}
                >
                  {t("production") || "Production"}
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center">
              <div className="hidden w-full h-full lg:block lg:w-3/5">
                <div className="px-26 py-17.5 text-center">
                  <span className="mt-15 inline-block">
                    <img className="h-full w-full object-cover" src={loginImage} alt="illustration" />
                  </span>
                </div>
              </div>

              <div className="w-full lg:w-2/5">
                <div className="w-full lg:w-3/4 p-4 sm:p-12.5 xl:p-17.5 m-auto">
                  <a className="mb-8 inline-block" href="">
                    <img src={logo} alt="Logo" />
                  </a>

                  <h2 className="mb-9 mt-3 text-3xl font-semibold text-dark">{t("signin")} </h2>

                  <form onSubmit={handleFormSubmit}>
                    <div className="mb-6">
                      <div className="relative">
                        <input
                          type="email"
                          placeholder={t("enterEmailToReceiveOtp")}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full rounded-lg bg-slate-50 py-4 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none placeholder-gray-500"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className="w-full cursor-pointer bg-blue-600 shadow-lg shadow-blue-300/50 rounded-lg border border-primary p-4 font-medium text-white transition hover:bg-blue-500 relative flex justify-center items-center"
                        disabled={loading}
                        style={{ height: '50px' }}
                      >
                        {loading ? (
                          <ReactLoading type="spin" color="#fff" height={24} width={24} />
                        ) : (
                          <>{t("otpsend")}</>
                        )}
                      </button>
                    </div>

                    {/* OR Divider */}
                    <div className="flex items-center my-6">
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <span className="px-4 text-sm text-gray-500">OR</span>
                      <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    <div className="flex flex-col gap-3">

                      {/* Guest */}
                      <button
                        type="button"
                        onClick={handleGuestLogin}
                        disabled={loading}
                        className="w-full h-[44px] flex items-center justify-center border border-gray-400 bg-white text-gray-700 rounded-md font-medium text-sm hover:bg-blue-50 transition relative" >
                        <img src={guest} alt="Guest" className="w-6 h-6 absolute left-2" />
                        <span className="ml-8">{t("signinGuest") || "Sign in as Guest"}</span>
                      </button>

                      {/* Google */}
                      <div className="relative w-full">
                        {/* Custom Google button */}
                        <button
                          type="button"
                          disabled={loading}
                          className="w-full h-[44px] flex items-center justify-center
    border border-gray-400 bg-white text-gray-700
    rounded-md font-medium text-sm 
    hover:bg-blue-50 transition relative"
                        >
                          <img src={google} alt="Google" className="w-6 h-6 absolute left-2" />
                          <span className="ml-8">
                            {t("signinGoogle") || "Sign in with Google"}
                          </span>
                        </button>

                        {/* Invisible Google real button */}
                        <div className="absolute inset-0 opacity-0">
                          <GoogleLogin
                            theme="outline"
                            size="large"
                            width="100%"
                            text="signin_with"
                            onSuccess={(credentialResponse) => {
                              const token = credentialResponse.credential;
                              if (token) {
                                handleGoogleLogin(token);
                              }
                            }}
                            onError={() => {
                              toast.error("Google login failed");
                            }}
                            useOneTap={false}
                          />
                        </div>
                      </div>

                      {/* Apple */}
                      <button
                        type="button"
                        onClick={handleAppleLogin}
                        className="w-full h-[44px] flex items-center justify-center border border-gray-400 bg-white text-gray-700 rounded-md font-medium text-sm hover:bg-blue-50 transition relative" >
                        <img src={apple} alt="Apple" className="w-6 h-6 absolute left-2" />
                        <span className="ml-8">{t("signinApple") || "Sign in with Apple"}</span>
                      </button>

                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ToastContainer />
    </div>
  );
};

export default Signin;
